import type { IObservableArray } from 'mobx';
import { runInAction } from 'mobx';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { type DisplayCreative, EIntegrationTypes } from '@feathr/blackbox';
import { toast } from '@feathr/components';
import { useStore } from '@feathr/extender/state';

// =============================================================================
// useDuplicateCreative
// =============================================================================

export interface IUseDuplicateCreativeParams {
  /** Campaign ID to associate cloned creative with. If omitted, creative is cloned without a campaign association. */
  campaignId?: string;
  /** Event ID for the cloned creative */
  eventId?: string;
  /** Observable array to add duplicated creatives to (for optimistic updates) */
  creatives?: IObservableArray<DisplayCreative>;
  /** Callback to trigger appear animation after adding to list */
  onAppear?: (id: string) => void;
  /** Callback when duplication completes successfully */
  onSuccess?: () => void;
}

export interface IUseDuplicateCreativeResult {
  /** Whether a duplication is in progress */
  isDuplicating: boolean;
  /** Duplicates a single creative by ID */
  duplicateCreative: (creativeId: string, name: string) => Promise<DisplayCreative | undefined>;
  /** Duplicates multiple creatives by ID */
  duplicateCreatives: (
    creatives: Array<{ id: string; name: string }>,
  ) => Promise<DisplayCreative[]>;
}

/**
 * Hook for duplicating creatives to a campaign.
 *
 * Handles cloning creatives via the API and refreshing the cache.
 * Can be used from the DuplicateCreativeModal or from individual CreativeCard menus.
 *
 * @param params - Hook parameters including campaignId, eventId, and callbacks
 * @returns Object with isDuplicating state and duplicate functions
 *
 * @example
 * // In a CreativeCard menu handler:
 * const { duplicateCreative, isDuplicating } = useDuplicateCreative({
 *   campaignId: campaign.id,
 *   eventId: campaign.get('event'),
 * });
 *
 * async function handleDuplicate() {
 *   await duplicateCreative(creative.id, `${creative.name} (copy)`);
 * }
 *
 * @example
 * // In the DuplicateCreativeModal:
 * const { duplicateCreatives, isDuplicating } = useDuplicateCreative({
 *   campaignId,
 *   eventId,
 *   onSuccess: onClose,
 * });
 *
 * async function handleQuickAdd() {
 *   await duplicateCreatives(selectedCreatives.map(c => ({ id: c.id, name: c.name })));
 * }
 */
export function useDuplicateCreative({
  campaignId: campaignIdProp,
  eventId: eventIdProp,
  creatives,
  onAppear,
  onSuccess,
}: IUseDuplicateCreativeParams = {}): IUseDuplicateCreativeResult {
  const { t } = useTranslation();
  const { Creatives } = useStore();
  const { eventId: eventIdParam, campaignId: campaignIdParam } = useParams<{
    eventId?: string;
    campaignId?: string;
  }>();

  const campaignId = campaignIdProp ?? campaignIdParam;
  const eventId = eventIdProp ?? eventIdParam;

  const [isDuplicating, setIsDuplicating] = useState(false);

  const duplicateCreative = useCallback(
    async (creativeId: string, name: string): Promise<DisplayCreative | undefined> => {
      setIsDuplicating(true);
      try {
        const creative = Creatives.get(creativeId);

        const cloneOverrides: Record<string, unknown> = {};
        if (eventId) {
          cloneOverrides.event = eventId;
        }
        cloneOverrides._parent = campaignId ?? null;
        // creative here is the base Creative type, not DisplayCreative;
        // the channel computed property lives on DisplayCreative, so we read
        // isFacebook directly off the base class.
        if (creative?.isFacebook) {
          cloneOverrides.integrations = [EIntegrationTypes.Meta];
          cloneOverrides._cls = (creative as DisplayCreative).get('_cls');
        }

        const cloned = await Creatives.clone(creativeId, {
          name,
          ...cloneOverrides,
        });

        const clonedCreative = cloned as DisplayCreative;

        // Optimistic update: add to front of list and animate
        if (creatives && clonedCreative) {
          runInAction(() => creatives.unshift(clonedCreative));
          onAppear?.(clonedCreative.id);
          // Delay toast until after poof-in animation completes
          setTimeout(() => {
            toast(t('Created "{{name}}".', { name }), { type: 'success' });
          }, 250);
        }

        return clonedCreative;
      } catch {
        toast(t("Couldn't duplicate creative. Try again."), { type: 'error' });
        return undefined;
      } finally {
        setIsDuplicating(false);
      }
    },
    [Creatives, campaignId, creatives, eventId, onAppear, t],
  );

  const duplicateCreatives = useCallback(
    async (creatives: Array<{ id: string; name: string }>): Promise<DisplayCreative[]> => {
      if (creatives.length === 0) {
        return [];
      }

      setIsDuplicating(true);
      try {
        const results: DisplayCreative[] = [];
        for (const creative of creatives) {
          const cloned = await duplicateCreative(creative.id, `${creative.name} (copy)`);
          if (cloned) {
            results.push(cloned);
          }
        }
        onSuccess?.();
        return results;
      } finally {
        setIsDuplicating(false);
      }
    },
    [duplicateCreative, onSuccess],
  );

  return {
    isDuplicating,
    duplicateCreative,
    duplicateCreatives,
  };
}

// =============================================================================
// useRemoveCreative
// =============================================================================

interface IDoRemoveParams {
  creative: DisplayCreative;
  creatives: IObservableArray<DisplayCreative>;
  creativesStore: { remove: (id: string) => void };
  t: (key: string, options?: Record<string, unknown>) => string;
}

async function doRemoveCreative({
  creative,
  creatives,
  creativesStore,
  t,
}: IDoRemoveParams): Promise<void> {
  const creativeId = creative.get('id');
  const creativeName = creative.get('name') ?? t('Creative');

  if (creative.isEphemeral) {
    runInAction(() => {
      creatives.remove(creative);
      creativesStore.remove(creativeId);
    });
    toast(t('Removed "{{name}}".', { name: creativeName }), { type: 'success' });
    return;
  }

  // Optimistic: remove from list immediately
  runInAction(() => {
    creatives.replace(creatives.filter((c) => c.get('id') !== creativeId));
  });

  // Then persist in background
  try {
    runInAction(() => creative.set({ is_archived: true }));
    await creative.patchDirty();
    toast(t('Deleted "{{name}}".', { name: creativeName }), { type: 'success' });
  } catch {
    // Rollback on failure
    runInAction(() => {
      creative.set({ is_archived: false });
      creatives.push(creative);
    });
    toast(t('Couldn\'t delete "{{name}}". Try again.', { name: creativeName }), { type: 'error' });
  }
}

export interface IUseRemoveCreativeParams {
  /** Observable array to remove the creative from */
  creatives?: IObservableArray<DisplayCreative>;
  /**
   * Animation callback from usePoof. When provided, the hook will use this
   * to coordinate animations instead of managing its own state.
   */
  onAnimateRemove?: (id: string, onComplete: () => void | Promise<void>) => Promise<void>;
}

export interface IUseRemoveCreativeResult {
  /**
   * Removes a creative from a campaign with a poof animation.
   * - Ephemeral creatives are deleted from store
   * - Persisted creatives are archived (soft delete via PATCH)
   * Shows toast on success/failure.
   */
  removeCreative: (creative: DisplayCreative) => Promise<void>;
}

/**
 * Hook for removing/archiving creatives from a campaign.
 *
 * Handles both ephemeral (unsaved) and persisted creatives:
 * - Ephemeral: Removes from observable array and store immediately
 * - Persisted: Sets is_archived=true and patches to API
 *
 * Shows toast notifications on success/failure.
 * Pass `onAnimateRemove` from `usePoof().animateRemove` to coordinate animations.
 *
 * @returns Object with removeCreative function
 *
 * @example
 * const { removingIds, appearingIds, animateRemove, animateAppear } = usePoof();
 * const { removeCreative } = useRemoveCreative({
 *   creatives,
 *   onAnimateRemove: animateRemove,
 * });
 *
 * <PoofGridItem id={creative.id} isRemoving={removingIds.has(creative.id)}>
 *   <CreativeCard ... />
 * </PoofGridItem>
 */
export function useRemoveCreative({
  creatives,
  onAnimateRemove,
}: IUseRemoveCreativeParams = {}): IUseRemoveCreativeResult {
  const { t } = useTranslation();
  const { Creatives } = useStore();

  const removeCreative = useCallback(
    async (creative: DisplayCreative): Promise<void> => {
      if (!creatives) {
        return;
      }

      const creativeId = creative.get('id');
      const doRemove = (): Promise<void> =>
        doRemoveCreative({ creative, creatives, creativesStore: Creatives, t });

      if (onAnimateRemove) {
        await onAnimateRemove(creativeId, doRemove);
      } else {
        await doRemove();
      }
    },
    [Creatives, creatives, onAnimateRemove, t],
  );

  return {
    removeCreative,
  };
}
